import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Package, Save, ScanBarcode, MapPin, Building2, Tag, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AddProductPage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [locations, setLocations] = useState([]);
  const eanInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    ean: "",
    artikelnummer: "",
    produktnamn: "",
    antal: 1,
    lagerplats: "",
    tillverkare: "",
    kategori: "",
    bokningsinfo: "",
  });

  useEffect(() => {
    fetchFilters();
    eanInputRef.current?.focus();
  }, []);

  // Listen for scanner input
  useEffect(() => {
    let buffer = "";
    let timeout = null;

    const handleKeyPress = (e) => {
      const activeElement = document.activeElement;
      
      // Only capture if EAN field is focused
      if (activeElement !== eanInputRef.current) return;

      if (e.key === "Enter") {
        if (buffer.length >= 8) {
          setFormData(prev => ({ ...prev, ean: buffer }));
          // Move focus to next field
          document.querySelector('[name="artikelnummer"]')?.focus();
        }
        buffer = "";
        e.preventDefault();
        return;
      }

      if (e.key.length === 1) {
        buffer += e.key;
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          buffer = "";
        }, 100);
      }
    };

    window.addEventListener("keypress", handleKeyPress);
    return () => {
      window.removeEventListener("keypress", handleKeyPress);
      clearTimeout(timeout);
    };
  }, []);

  const fetchFilters = async () => {
    try {
      const [catRes, mfgRes, locRes] = await Promise.all([
        axios.get(`${API}/inventory/categories`),
        axios.get(`${API}/inventory/manufacturers`),
        axios.get(`${API}/inventory/locations`)
      ]);
      setCategories(catRes.data);
      setManufacturers(mfgRes.data);
      setLocations(locRes.data);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  const handleSave = async () => {
    if (!formData.ean.trim()) {
      toast.error("EAN-kod krävs");
      return;
    }
    if (!formData.lagerplats.trim()) {
      toast.error("Lagerplats krävs");
      return;
    }

    setSaving(true);
    try {
      // Use the scan endpoint to create
      await axios.post(`${API}/inventory/scan`, {
        ean: formData.ean.trim(),
        lagerplats: formData.lagerplats.trim(),
        antal: formData.antal
      });

      // If additional fields provided, update the item
      if (formData.artikelnummer || formData.produktnamn || formData.tillverkare || formData.kategori) {
        const items = await axios.get(`${API}/inventory/by-ean/${formData.ean.trim()}`);
        const newItem = items.data.find(i => i.lagerplats === formData.lagerplats.trim());
        
        if (newItem) {
          await axios.put(`${API}/inventory/${newItem.id}`, {
            artikelnummer: formData.artikelnummer,
            produktnamn: formData.produktnamn,
            tillverkare: formData.tillverkare,
            kategori: formData.kategori,
            bokningsinfo: formData.bokningsinfo,
          });
        }
      }

      toast.success("Produkt skapad!");
      navigate("/inventory");
    } catch (error) {
      toast.error("Kunde inte spara: " + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/inventory">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-xl font-bold uppercase tracking-tight">
                  Ny produkt
                </h1>
                <p className="text-sm text-muted-foreground">
                  Lägg till produkt via EAN-skanning
                </p>
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              <Save className="h-4 w-4" />
              {saving ? "Sparar..." : "Spara"}
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <div className="max-w-2xl mx-auto space-y-6">
          
          {/* EAN Scanning */}
          <Card className="border-primary/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ScanBarcode className="h-5 w-5 text-primary" />
                EAN-kod *
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Input
                ref={eanInputRef}
                value={formData.ean}
                onChange={(e) => setFormData({...formData, ean: e.target.value})}
                placeholder="Skanna eller skriv EAN..."
                className="text-2xl font-mono h-16 text-center"
                data-testid="ean-input"
              />
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Skanna streckkod med Zebra-scanner eller skriv in manuellt
              </p>
            </CardContent>
          </Card>

          {/* Location */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="h-5 w-5" />
                Lagerplats *
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Input
                value={formData.lagerplats}
                onChange={(e) => setFormData({...formData, lagerplats: e.target.value.toUpperCase()})}
                placeholder="T.ex. A1, B2-PL3..."
                className="text-xl font-mono"
                list="locations-list"
              />
              <datalist id="locations-list">
                {locations.map(l => <option key={l.lagerplats} value={l.lagerplats} />)}
              </datalist>
            </CardContent>
          </Card>

          {/* Product Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="h-5 w-5" />
                Produktinformation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Artikelnummer</Label>
                  <Input
                    name="artikelnummer"
                    value={formData.artikelnummer}
                    onChange={(e) => setFormData({...formData, artikelnummer: e.target.value})}
                    placeholder="T.ex. ABC-123"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Antal</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.antal}
                    onChange={(e) => setFormData({...formData, antal: parseInt(e.target.value) || 1})}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Produktnamn / Beskrivning</Label>
                <Textarea
                  value={formData.produktnamn}
                  onChange={(e) => setFormData({...formData, produktnamn: e.target.value})}
                  placeholder="Beskrivning av produkten..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    Tillverkare
                  </Label>
                  <Input
                    value={formData.tillverkare}
                    onChange={(e) => setFormData({...formData, tillverkare: e.target.value})}
                    placeholder="T.ex. Bosch"
                    list="manufacturers-list"
                  />
                  <datalist id="manufacturers-list">
                    {manufacturers.map(m => <option key={m} value={m} />)}
                  </datalist>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Tag className="h-3 w-3" />
                    Kategori
                  </Label>
                  <Input
                    value={formData.kategori}
                    onChange={(e) => setFormData({...formData, kategori: e.target.value})}
                    placeholder="T.ex. Tvättmaskin"
                    list="categories-list"
                  />
                  <datalist id="categories-list">
                    {categories.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Bokningsinfo</Label>
                <Textarea
                  value={formData.bokningsinfo}
                  onChange={(e) => setFormData({...formData, bokningsinfo: e.target.value})}
                  placeholder="Eventuell bokningsinformation..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
